/**
 * adapters/telemetry/errorReporter — sink de erros de componentes React para
 * o endpoint de telemetria configurado (DD-010, RNF 6.2).
 *
 * Comportamento:
 *  - Usa o mesmo endpoint de `VITE_VITALS_ENDPOINT` (ou `VITE_ERROR_ENDPOINT`
 *    se definido separadamente) para enviar `ErrorReport` saneados.
 *  - Aplica `sanitizeForTelemetry` antes do envio para garantir ausência de
 *    PII/token no payload.
 *  - Se nenhum endpoint estiver configurado, opera como no-op silencioso.
 *  - Usa `navigator.sendBeacon` quando disponível; `fetch` como fallback
 *    best-effort. Nenhuma falha de envio propaga para a UI.
 */

import { sanitizeForTelemetry } from "./sanitize.js";
import type { ErrorReport } from "../../app/ErrorBoundary.js";

/** Envia o payload de erro ao endpoint de forma best-effort (não bloqueia UI). */
function sendErrorPayload(endpoint: string, payload: unknown): void {
  const body = JSON.stringify(payload);
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(endpoint, body);
    } else {
      fetch(endpoint, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/json" },
        keepalive: true,
      }).catch(() => {
        // best-effort — falhas não impactam a UI
      });
    }
  } catch {
    // best-effort — nunca propaga erro para o caller
  }
}

/**
 * Cria um sink de telemetria para erros de componentes React capturados pelo
 * `ErrorBoundary`. Retorna uma função compatível com a prop `onReportError`.
 *
 * Se nenhum endpoint estiver configurado (`VITE_ERROR_ENDPOINT` ou
 * `VITE_VITALS_ENDPOINT`), retorna um no-op silencioso.
 *
 * @example
 * const reportError = createErrorReporter();
 * <ErrorBoundary onReportError={reportError}>...</ErrorBoundary>
 */
export function createErrorReporter(): (report: ErrorReport) => void {
  const endpoint =
    (import.meta.env.VITE_ERROR_ENDPOINT as string | undefined) ||
    (import.meta.env.VITE_VITALS_ENDPOINT as string | undefined);

  if (!endpoint) {
    // No-op silencioso quando nenhum endpoint está configurado.
    return () => undefined;
  }

  return (report: ErrorReport): void => {
    const safePayload = sanitizeForTelemetry({
      ...report,
      kind: "component-error",
    });
    sendErrorPayload(endpoint, safePayload);
  };
}
